<template>
  <div class="settings-users">
    <div class="settings-users__header">
      <div class="settings-users__hint">
        <div class="settings-users__hint-icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="9" cy="9" r="9" fill="#DBE7FD" />
            <path
              d="M9.01934 14.4C8.75299 14.4 8.52101 14.3036 8.3234 14.1108C8.12578 13.9089 8.02698 13.6702 8.02698 13.3949C8.02698 13.1103 8.12149 12.867 8.31051 12.6651C8.50812 12.4631 8.7401 12.3622 9.00645 12.3622C9.28139 12.3622 9.51767 12.4631 9.71528 12.6651C9.9129 12.8579 10.0117 13.0965 10.0117 13.3811C10.0117 13.6565 9.9129 13.8951 9.71528 14.0971C9.52626 14.299 9.29428 14.4 9.01934 14.4Z"
              fill="white"
            />
            <path d="M8 4.5H10V11.5H8V4.5Z" fill="white" />
          </svg>
        </div>
        <div class="settings-users__hint-text">
          {{ $t('settings.users.hints.main') }}
        </div>
      </div>
      <search-input
        class="settings-users__search"
        v-model:value="searchQuery"
        :title="$t('settings.users.fields.search')"
      ></search-input>
      <require-access data="settings-users-create" action="read">
        <primary-button
          class="settings-users__create"
          :label="$t('settings.users.actions.create')"
          @click="toCreateUser"
        ></primary-button>
      </require-access>
    </div>
    <div class="settings-users__loading" v-if="isUsersLoading">
      <loader></loader>
    </div>
    <div class="settings-users__content" v-else>
      <div class="settings-users__list">
        <user-item
          v-for="user in usersFiltered"
          :key="`users-${user.user_id}`"
          :user="user"
          @userDeleted="handleUserDeleted"
        ></user-item>
      </div>
      <!--      <pagination-->
      <!--        class="settings-users__pagination"-->
      <!--        :current-page="pagination.currentPage"-->
      <!--        :per-page="pagination.perPage"-->
      <!--        :items-count="usersFiltered.length"-->
      <!--        @changePage="handlePageChanged"-->
      <!--      ></pagination>-->
    </div>
    <success-notification
      ref="userDeletedSuccessNotification"
      :text="$t('settings.users.notifications.user-deleted')"
    ></success-notification>
  </div>
</template>

<script>
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import Pagination from '@vue-app/components/base/control/Pagination.vue'
import SearchInput from '@vue-app/components/base/input/SearchInput.vue'
import { mapState } from 'vuex'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import UserItem from '@vue-app/components/company/settings/users/UserItem.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'

export default {
  name: 'SettingsUsers',
  components: {
    RequireAccess,
    SuccessNotification,
    Loader,
    UserItem,
    SearchInput,
    Pagination,
    PrimaryButton,
  },
  data() {
    return {
      users: [],
      isUsersLoading: true,
      pagination: {
        currentPage: 1,
        perPage: 10,
      },
      searchQuery: '',
    }
  },
  computed: {
    ...mapState(['selectedCompany']),
    userPaged() {
      let _startFrom =
        (this.pagination.currentPage - 1) * this.pagination.perPage
      return this.usersFiltered.slice(
        _startFrom,
        _startFrom + this.pagination.perPage
      )
    },
		usersFiltered() {
			const norm = (s) =>
				(s ?? "")
					.toString()
					.normalize("NFC")
					.toLocaleLowerCase("uk")
					.trim();

			const q = norm(this.searchQuery);
			if (!q) return this.users;

			const tokens = q.split(/\s+/).filter(Boolean);
			if (tokens.length === 0) return this.users;

			return this.users.filter((_user) => {
				const last = _user.last_name ?? "";
				const first = _user.first_name ?? "";
				const email = _user.email ?? "";
				const phone = _user.cell_phone ?? "";

				const haystack = norm(
					`${last} ${first} ${first} ${last} ${email} ${phone} ${phone.replace(/\D/g, "")}`
				);

				return tokens.every((t) => haystack.includes(t));
			});
		},
  },
  watch: {
    usersFiltered() {
      this.pagination.currentPage = 1
    },
  },
  created() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      try {
        let _usersResponse = await CompaniesApi.getUsers(this.selectedCompany)
        this.users = Array.isArray(_usersResponse.data)
          ? _usersResponse.data
          : []
      } catch (e) {
      } finally {
        this.isUsersLoading = false
      }
    },
    toCreateUser() {
      this.$router.push({
        name: 'create-company-user',
        params: {
          id: this.selectedCompany,
        },
      })
    },
    handlePageChanged(page) {
      this.pagination.currentPage = page
    },
    handleUserDeleted(user) {
      this.users.splice(
        this.users.findIndex((_u) => _u.user_id === user.user_id),
        1
      )
      this.$refs.userDeletedSuccessNotification.show()
    },
  },
}
</script>

<style lang="scss" scoped>
.settings-users {
  &__header {
    display: flex;
    align-items: center;
  }

  &__hint {
    display: flex;
    align-items: flex-start;
    flex-shrink: 1;
    flex-grow: 1;
    margin-right: 50px;

    &-icon {
      width: 18px;
      height: 18px;

      margin-right: 12px;
    }

    &-text {
      font-weight: 400;
      font-size: 12px;
      line-height: 18px;
      color: #67768a;
    }
  }

  &__search {
    width: 188px;
    flex-shrink: 0;
    flex-grow: 0;
    margin-right: 15px;
  }

  &__create {
    width: 249px;
    flex-shrink: 0;
    flex-grow: 0;
  }

  &__content {
    margin-top: 30px;
  }

  &__list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 14px;
    row-gap: 15px;
  }

  &__pagination {
    margin-top: 25px;
  }
}
</style>
